import React, { useState, useEffect, useCallback, FunctionComponent, FormEvent } from 'react';
import { Presentation } from './presentation';
import { CompanyForm, PlanPurchaseForm, UserAuthForm } from '../../../types/companyRegisterForm';
import { getCsrfToken } from '../../../libs/csrfToken';
import { createPlanAndCompany } from '../../../services/companies';
import { getHubspotUtk } from '../../../libs/hubspotUtk';
import { NextPlanInfo } from '../../../types/companyAccount';
import { fetchPlanOptions } from '../../../services/plan';

type Props = {
  handleFlash: (flash: boolean, type: string, message: string) => void;
  company: CompanyForm;
  userAuth: UserAuthForm;
  planPurchase: PlanPurchaseForm;
  handleChange: (section: 'company' | 'userAuth' | 'planPurchase', field: string, value: string | boolean | number) => void;
  recaptchaSiteKey: string;
};

export const Component: FunctionComponent<Props> = ({
  handleFlash,
  company,
  userAuth,
  planPurchase,
  handleChange,
  recaptchaSiteKey,
}) => {
  const[isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const handleRecaptcha = useCallback(
    (token: string | null) => {
      setRecaptchaToken(token);
    },
    []
  );

  const createEmptyPlan = (): NextPlanInfo => ({
    planName: '',
    planTerm: null,
    contractPeriodStart: '',
    contractPeriodEnd: '',
    canPostRecruitments: false,
    canUseLikes: false,
    canUseScouts: false,
    billingDate: '',
    amountBilled: '',
    paymentDueDate: '',
    contractPlanId: 0,  // 初期化時にはあえて存在しない0を
  });
  const createInitialPlans = (): NextPlanInfo[] =>
    Array.from({ length: 6 }, createEmptyPlan);
  const [planOptions, setPlanOptions] = useState<NextPlanInfo[]>(createInitialPlans());

  useEffect(() => {
    const fetchPlanData = async () => {
      setIsLoading(true);
      try {
        const response = await fetchPlanOptions();
        setPlanOptions(response.planOptions.filter((card) =>card.planName !== 'トライアルプラン'));
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPlanData();
  }, []);

  const validatePlanForm = (): boolean => {
    if (!planPurchase.contractPlanId) {
      handleFlash(true, 'danger', 'プランが未選択です。');
      return false;
    }

    const missingFields: string[] = [];

    if (!planPurchase.companyName || planPurchase.companyName.trim() === '') missingFields.push('会社名');
    if (!planPurchase.department || planPurchase.department.trim() === '') missingFields.push('担当部署');
    if (!planPurchase.inCharge || planPurchase.inCharge.trim() === '') missingFields.push('ご担当者名');
    if (!planPurchase.zipCode01 || planPurchase.zipCode01.trim() === '' ||
        !planPurchase.zipCode02 || planPurchase.zipCode02.trim() === '') {
      missingFields.push('会社郵便番号');
    }
    if (!planPurchase.address01 || planPurchase.address01.trim() === '') missingFields.push('会社住所（番地まで）');
    if (!planPurchase.email || planPurchase.email.trim() === '') missingFields.push('請求書送付先メールアドレス');

    if (missingFields.length > 0) {
      handleFlash(true, 'danger', `${missingFields.join(' / ')} が未入力です。`);
      return false;
    }

    if (userAuth.password && userAuth.passwordConfirmation && userAuth.password !== userAuth.passwordConfirmation) {
      handleFlash(true, 'danger', '確認パスワードが一致しません');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setIsSubmitting(true);

    const isValid = validatePlanForm();
    if (!isValid) {
      setIsLoading(false);
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    // user_auth_attributes
    formData.append('user[user_auth_attributes][email]', userAuth.email || '');
    formData.append('user[user_auth_attributes][password]', userAuth.password || '');
    formData.append('user[user_auth_attributes][password_confirmation]', userAuth.passwordConfirmation || '');
    // company_attributes
    formData.append('user[company_attributes][name]', company.name || '');
    formData.append('user[company_attributes][phone_number]', company.phoneNumber || '');
    formData.append('user[company_attributes][staff_name]', company.staffName || '');
    formData.append('user[company_attributes][url]', company.url || '');
    formData.append('user[company_attributes][oshiri]', company.oshiri || '');
    formData.append('user[company_attributes][riyohoho]', company.riyohoho || '');
    // company_attributes plan_purchase
    formData.append('user[company_attributes][plan_purchase][contract_plan_id]', String(planPurchase.contractPlanId || ''));
    formData.append('user[company_attributes][plan_purchase][payment_method_id]', String(planPurchase.paymentMethodId || ''));
    formData.append('user[company_attributes][plan_purchase][invitation_code]', planPurchase.invitationCode || '');
    formData.append('user[company_attributes][plan_purchase][company_name]', planPurchase.companyName || '');
    formData.append('user[company_attributes][plan_purchase][department]', planPurchase.department || '');
    formData.append('user[company_attributes][plan_purchase][in_charge]', planPurchase.inCharge || '');
    formData.append('user[company_attributes][plan_purchase][zip_code01]', planPurchase.zipCode01 || '');
    formData.append('user[company_attributes][plan_purchase][zip_code02]', planPurchase.zipCode02 || '');
    formData.append('user[company_attributes][plan_purchase][address01]', planPurchase.address01 || '');
    formData.append('user[company_attributes][plan_purchase][address02]', planPurchase.address02 || '');
    formData.append('user[company_attributes][plan_purchase][email]', planPurchase.email || '');
    formData.append('user[company_attributes][plan_purchase][email_sub_first]', planPurchase.emailSubFirst || '');
    formData.append('user[company_attributes][plan_purchase][email_sub_second]', planPurchase.emailSubSecond || '');
    formData.append('user[company_attributes][plan_purchase][continuation_flag]', '1');  // ここは新規登録フォームのため、固定で1
    formData.append('user[company_attributes][plan_purchase][agreement]', planPurchase.agreement ? '1' : '0');
    // hubspotutk
    formData.append('user[hubspotutk]', getHubspotUtk());

    try {
      const response = await createPlanAndCompany(formData, getCsrfToken());
      if (response.errorMessages) {
        setIsLoading(false);
        setIsSubmitting(false);
        handleFlash(true, 'danger', response.errorMessages);
        return;
      }
      window.location.href = '/companies/complete_registration'
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Presentation
      planPurchase={planPurchase}
      isLoading={isLoading}
      isSubmitting={isSubmitting}
      planOptions={planOptions}
      recaptchaToken={recaptchaToken}
      recaptchaSiteKey={recaptchaSiteKey}
      handleChange={handleChange}
      onSubmit={handleSubmit}
      onRecaptcha={handleRecaptcha}
    />
  );
};
